"use client";
/**
 * The demonstration, in one frame box that never changes size.
 *
 * The poster and the player are the SAME box at the same ratio: the kit's media
 * frame swaps its own contents when a reader arms it, so nothing below the
 * frame moves when the video appears. The frame's shape comes from the measured
 * asset — 1280×720, probed before this file was drawn — rather than from the
 * projection's `orientation` field, which says `portrait` about a clip that is
 * demonstrably landscape.
 *
 * Five states share the box, and only one of them is an error.
 *
 *   Playable   · poster, a play mark, and an armed native player.
 *   Expired    · poster and NO arm. The clip host signs every URL for exactly
 *                48 hours; the poster lives on a different, unsigned host, so
 *                the picture is still real after the video is a 403. Arming it
 *                would put a broken player where a working one used to be.
 *   Unresolved · the catalog says a clip exists and publishes no address for
 *                it. There is nothing to show and nothing to fetch, so the
 *                frame carries the item's designed absence and says why.
 *   Absent     · nobody filmed this movement. Same box, different sentence.
 *   Failed     · the reader armed it and the element refused the source.
 *
 * The clock is state rather than a value read during render, and the transition
 * into `Expired` is scheduled at the exact instant the signature dies. A frame
 * that decided its own expiry once at mount would keep offering a dead link for
 * as long as the tab stayed open.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import {
  CresultcardMedia,
  CresultcardMediaKind,
  CresultcardMediaOverlay,
  CRC_R_PILL,
  CRC_TEXT,
  CresultcardMediaScale,
} from "@/components/ui/Cresultcard";
import {
  CWORKOUTCARD_WATCH_LABEL,
  C_WORKOUT_POSTER_INTRINSIC_HEIGHT,
  C_WORKOUT_POSTER_INTRINSIC_WIDTH,
  C_WORKOUT_VIDEO_STATE_LABEL,
} from "./cworkoutcard.constants";
import {
  cworkoutcardIsPlayable,
  cworkoutcardPoster,
  cworkoutcardPrimaryClip,
  cworkoutcardVideoState,
} from "./cworkoutcard.model";
import type { CworkoutcardData } from "./cworkoutcard.types";
import { CworkoutcardVideoState } from "./cworkoutcard.types";

/** `setTimeout` silently fires immediately past this, so a longer wait is not armed. */
const MAX_TIMER_MS = 2_147_483_647;

const PLAYER_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "block",
  width: "100%",
  height: "100%",
  border: "none",
  // The frame already wears the clip's own ratio, so `contain` neither crops
  // nor letterboxes — it is insurance for a clip whose pixels differ from the
  // poster the frame was measured from.
  objectFit: "contain",
  backgroundColor: "#000000",
};

const PLAY_MARK_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: CRC_R_PILL,
  backgroundColor: "rgba(4,16,18,0.72)",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.28)",
  color: CRC_TEXT,
};

const PLAY_MARK_GLYPH_SIZE = 19;

interface CworkoutcardVideoProps {
  readonly data: CworkoutcardData;
  readonly scale: CresultcardMediaScale;
  /** A caption under the frame — the shoot, the length, the expiry sentence. */
  readonly caption?: ReactNode;
}

/**
 * The instant the frame is reasoning about, and the transition into the next
 * one.
 *
 * A second value is not scheduled after the first fires: past `expiresAt` there
 * is no further state for this frame to reach.
 */
function useExpiryClock(expiresAt: string | null): Date {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect((): (() => void) | undefined => {
    if (expiresAt == null) {
      return undefined;
    }
    const deadline = Date.parse(expiresAt);
    if (Number.isNaN(deadline)) {
      return undefined;
    }
    const delay = deadline - Date.now();
    if (delay <= 0 || delay > MAX_TIMER_MS) {
      return undefined;
    }
    const timer = window.setTimeout((): void => {
      setNow(new Date());
    }, delay);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [expiresAt]);

  return now;
}

export function CworkoutcardVideo({
  data,
  scale,
  caption,
}: CworkoutcardVideoProps): ReactElement {
  const [playbackFailed, setPlaybackFailed] = useState(false);
  // Hoisted: a hook dependency has to be a bare local identifier or the React
  // Compiler bails out of optimising the whole component.
  const title = data.title;
  const now = useExpiryClock(data.media?.expiresAt ?? null);
  const clip = cworkoutcardPrimaryClip(data.media);
  const poster = cworkoutcardPoster(data.media);
  const state = cworkoutcardVideoState({ data, now, playbackFailed });
  const playable = cworkoutcardIsPlayable(state);

  const handleError = useCallback((): void => {
    setPlaybackFailed(true);
  }, []);

  const renderPlayer = useCallback((): ReactNode => {
    if (clip == null) {
      return null;
    }
    return (
      <video
        autoPlay
        controls
        data-el="Cworkoutcard/Video/Player"
        data-source-url={clip.videoUrl}
        onError={handleError}
        playsInline
        src={clip.videoUrl}
        style={PLAYER_STYLE}
      />
    );
  }, [clip, handleError]);

  const arm = useMemo(
    () =>
      playable && clip != null ? { title, render: renderPlayer } : undefined,
    [clip, playable, renderPlayer, title],
  );

  // The poster is only shown where it can still mean something. On `Failed` the
  // reader has already seen the player refuse; putting its own poster back
  // would read as the frame recovering.
  const posterSrc =
    state === CworkoutcardVideoState.Failed
      ? undefined
      : (poster?.src ?? undefined);

  return (
    <span
      data-el="Cworkoutcard/Video/Root"
      data-video-state={state}
      style={{ display: "block", minWidth: 0 }}
    >
      <CresultcardMedia
        alt={`${CWORKOUTCARD_WATCH_LABEL} ${title}`}
        arm={arm}
        fallbackSecondary={C_WORKOUT_VIDEO_STATE_LABEL[state]}
        fallbackTitle={title}
        // A clip with no poster still needs a box, and a `Native` frame with no
        // declared intrinsics falls back to the kit's least-committal 4:3 —
        // which is not this vendor's shape. The measured pair is the family's
        // own: `curl` returned a 1280×720 JPEG and `ffprobe` a 1280×720 clip
        // off `ymove.exerciseMedia`. A published poster still wins, because its
        // pixels are this clip's rather than the population's.
        intrinsicHeight={poster?.intrinsicHeight ?? C_WORKOUT_POSTER_INTRINSIC_HEIGHT}
        intrinsicWidth={poster?.intrinsicWidth ?? C_WORKOUT_POSTER_INTRINSIC_WIDTH}
        // `Native` rather than `Frame`: a demonstration clip's shape IS the
        // information — a floor movement and a standing one are not filmed the
        // same way — and `Frame`'s fixed 16:9 with `cover` would crop whichever
        // one disagreed with it.
        kind={CresultcardMediaKind.Native}
        overlay={
          playable ? (
            <CresultcardMediaOverlay
              centre={
                <span aria-hidden style={PLAY_MARK_STYLE}>
                  <IconPlayerPlayFilled size={PLAY_MARK_GLYPH_SIZE} />
                </span>
              }
              slots={{}}
            />
          ) : undefined
        }
        scale={scale}
        src={posterSrc}
      />
      {caption}
    </span>
  );
}

/** The frame's own state, for a composition that draws a caption beside it. */
export function useCworkoutcardVideoState(
  data: CworkoutcardData,
): CworkoutcardVideoState {
  const now = useExpiryClock(data.media?.expiresAt ?? null);
  return cworkoutcardVideoState({ data, now, playbackFailed: false });
}
